import {
  ModulesProvider,
  useDomains,
  useModules,
} from '@schamburg-web/components';
import React, { useEffect, useMemo, useRef } from 'react';
import uimodules from '../../modules';
import { Box } from '@mui/material';
import { MapLayer, ModuleConfig } from '@schamburg-web/interfaces';

interface ModulesContainerProps {
  children?: React.ReactNode;
}

const ModulesContainer: React.FC<ModulesContainerProps> = ({ children }) => {
  return (
    <ModulesProvider>
      <ModulesInnerContainer>{children}</ModulesInnerContainer>
    </ModulesProvider>
  );
};

const ModulesInnerContainer: React.FC<ModulesContainerProps> = ({
  children,
}) => {
  const { setModules, setModulesEnabled, modulesEnabled } = useModules();
  const { domains } = useDomains();
  const initialized = useRef(false);

  const maplayers = useMemo(() => {
    return JSON.parse(
      domains.find((d) => d.name === 'extWebsite.config.maplayers')?.value ??
        '{}'
    );
  }, [domains]);

  useEffect(() => {
    if (maplayers.length > 0 && !initialized.current) {
      initialized.current = true;

      const modulesWithMaplayers = uimodules
        .filter((module) =>
          maplayers.some(
            (layer: MapLayer) =>
              layer.layerName === module.name && layer.enabled
          )
        )
        .map((module) => {
          const layer = maplayers.find(
            (l: MapLayer) => l.layerName === module.name
          );
          return layer
            ? {
                ...module,
                mapConfig: {
                  ...module.mapConfig,
                  defaultEnabled: layer.defaultOn,
                },
              }
            : module;
        });
      setModules(modulesWithMaplayers);

      // Set module enabled states after modules are loaded
      const stored = sessionStorage.getItem('modulesEnabled');
      let parsedStored: Record<string, boolean> = {};

      try {
        if (stored) {
          parsedStored = JSON.parse(stored);
        }
      } catch (error) {
        console.warn('Failed to read from session storage:', error);
      }

      modulesWithMaplayers.forEach((module: ModuleConfig) => {
        // Use session storage value if it exists, otherwise use domain default
        const enabled =
          parsedStored[module.name] !== undefined
            ? parsedStored[module.name]
            : module.mapConfig.defaultEnabled;

        setModulesEnabled(module.name, enabled);
      });
    }
  }, [maplayers, setModules, setModulesEnabled]);

  // Save modulesEnabled to session storage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('modulesEnabled', JSON.stringify(modulesEnabled));
    } catch (error) {
      console.warn('Failed to save modulesEnabled to session storage:', error);
    }
  }, [modulesEnabled]);

  return <Box sx={{ height: '100%', width: '100%' }}>{children}</Box>;
};

export default ModulesContainer;
